import styled, { css } from 'styled-components';
import { Dayjs } from 'dayjs';
import { DatePicker } from 'antd';

import {
  RangePickerComponentProps,
  DataPickerComponentProps,
  TimePickerComponentProps,
  PickerComponentSpecialProps,
} from './types';

import { colors } from '@10d/tend-ui-tokens/samolet';

const headerButton = css`
  .ant-picker-header-prev-btn,
  .ant-picker-header-next-btn {
    width: 32px;
    height: 32px;
    border: 1px solid #eaedf5;
    border-radius: 8px;
    transition: all 0.3s ease-out;
    font-size: 16px;

    span {
      color: ${colors.blue600};
    }

    &:hover {
      border-color: ${colors.blue600};
    }

    &:active {
      border-color: #eaedf5;
      background-color: ${colors.blue100};
    }
  }

  .ant-picker-next-icon {
    transform: translate(-2px) rotate(135deg);
  }

  .ant-picker-prev-icon {
    transform: translate(2px) rotate(-45deg);
  }
`;

const dropdownHeader = css`
  .ant-picker-header {
    align-items: center;
    padding: 8px 16px 7px;
    border-bottom-color: #d7dce9;

    button {
      line-height: inherit;
    }

    .ant-picker-header-view {
      font-size: 14px;
      font-weight: 600;
      color: ${colors.blue600};
      line-height: 32px;

      button {
        text-transform: uppercase;
        margin-left: 0;

        &:last-child:before {
          content: '\\00a0';
        }
      }
    }

    .ant-picker-header-super-prev-btn,
    .ant-picker-header-super-next-btn {
      display: none;
    }

    ${headerButton}
  }
`;

const bodyWeekDay = css`
  .ant-picker-content th {
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    color: ${colors.gray650};
    width: 40px;

    &:last-child {
      width: 32px;
    }
  }

  .ant-picker {
    &-dropdown-range {
      padding-top: 0;
    }

    &-panel {
      border-bottom: none;
    }

    &-content {
      th {
        line-height: 32px;
      }

      th:not(:last-child),
      tr:not(:last-child) td {
        padding: 0 8px 8px 0;
      }

      tr:last-child td {
        padding: 0;
      }

      th:last-child {
        padding: 0 0 8px 0;
      }
    }

    &-date-panel {
      .ant-picker-content {
        width: 272px;
      }
      .ant-picker-body {
        padding: 8px 16px 16px;
      }
      .ant-picker-cell:not(:last-child) {
        padding-right: 8px;
      }
    }
  }
`;

const bodyOtherModes = css`
  .ant-picker-decade-panel .ant-picker-cell,
  .ant-picker-year-panel .ant-picker-cell,
  .ant-picker-quarter-panel .ant-picker-cell,
  .ant-picker-month-panel .ant-picker-cell {
    .ant-picker-cell-inner {
      min-width: 66px;
      height: 56px;
      line-height: 56px;
      border-radius: 8px;
    }

    &:hover:not(.ant-picker-cell-in-view) .ant-picker-cell-inner,
    &:hover:not(.ant-picker-cell-selected):not(.ant-picker-cell-range-start):not(
        .ant-picker-cell-range-end
      ):not(.ant-picker-cell-range-hover-start):not(.ant-picker-cell-range-hover-end)
      .ant-picker-cell-inner {
      color: ${colors.blue500};
    }
  }

  .ant-picker-decade-panel .ant-picker-cell .ant-picker-cell-inner {
    min-width: 68px;
    font-size: 12px;
  }

  .ant-picker {
    &-decade-panel,
    &-year-panel,
    &-month-panel,
    &-date-panel {
      width: 304px;
    }

    &-decade-panel,
    &-year-panel,
    &-month-panel {
      .ant-picker-body {
        padding: 8px 24px;
      }

      tr:not(:last-child) {
        td:not(:last-child) {
          padding: 0 29px 8px 0;
          width: 94px;
        }
        td:last-child {
          padding: 0 0 8px 0;
        }
      }

      tr:last-child td:not(:last-child) {
        padding: 0 29px 0px 0;
        width: 94px;
      }
    }
  }
`;

const bodyCommon = css`
  .ant-picker-cell {
    color: ${colors.gray400};

    &:hover:not(.ant-picker-cell-in-view) .ant-picker-cell-inner,
    &:hover:not(.ant-picker-cell-selected):not(.ant-picker-cell-range-start):not(
        .ant-picker-cell-range-end
      ):not(.ant-picker-cell-range-hover-start):not(.ant-picker-cell-range-hover-end)
      .ant-picker-cell-inner {
      background: ${colors.blue100};
    }

    &.ant-picker-cell-disabled::before {
      color: inherit;
      background: inherit;
    }

    &.ant-picker-cell-disabled .ant-picker-cell-inner {
      border-radius: 8px;
      color: ${colors.gray400};
      background: #eaedf5;
    }

    &.ant-picker-cell-in-view {
      color: ${colors.gray900};
      &.ant-picker-cell-today .ant-picker-cell-inner::before {
        border-color: ${colors.blue600};
        border-radius: 8px;
      }

      &.ant-picker-cell-selected .ant-picker-cell-inner,
      &.ant-picker-cell-range-start .ant-picker-cell-inner,
      &.ant-picker-cell-range-end .ant-picker-cell-inner {
        color: ${colors.gray0};
        background: ${colors.blue600};
      }

      &.ant-picker-cell-range-hover {
        &:after,
        &-start::after,
        &-end::after {
          width: 40px;
          height: 32px;
          top: 0;
          left: -2px;
          transform: translateY(0);
        }

        &-end::after {
          width: 34px;
          left: -4px;
        }

        &-start::after {
          width: 34px;
          left: 4px;
        }

        &-start:not(.ant-picker-cell-range-start-single)::after {
          border-radius: 8px 0 0 8px;
          left: 0;
          height: 32px !important;
          top: 40% !important;
        }

        // TODO
        // из-за JSS не получается некоторые свойства переназначить
        // пока так оставлю
        &-end:not(.ant-picker-cell-range-end-single)::after {
          border-radius: 0px 8px 8px 0px;
          height: 32px !important;
          top: 40% !important;
        }
      }
    }
  }
`;

const bodyDateWeekMode = css`
  .ant-picker-week-panel .ant-picker-cell,
  .ant-picker-date-panel .ant-picker-cell {
    font-size: 12px;
    line-height: 16px;

    &::before {
      height: 32px;
    }

    .ant-picker-cell-inner {
      min-width: 32px;
      height: 32px;
      line-height: 32px;
      border-radius: 8px;
    }
  }
`;

const dropdownBody = css`
  ${bodyCommon}
  ${bodyDateWeekMode}
  ${bodyOtherModes}
  ${bodyWeekDay}
`;

const dropdownFooter = css`
  .ant-picker-footer {
    border-top-color: #d7dce9;
    border-bottom: none;

    &-extra {
      line-height: 44px;
    }
  }
`;

export const dropdown = css`
  .ant-picker {
    &-cell-range-hover:after,
    &-cell::before {
      top: 0;
      height: 32px;
      transform: translateY(0);
    }

    &-dropdown-range {
      padding-top: 0;
    }

    &-range-arrow {
      display: none;
    }
  }

  & .ant-picker-panel-container {
    border-radius: 12px;
    overflow: hidden;

    ${dropdownHeader}
    ${dropdownBody}
    ${dropdownFooter}
  }
`;

const input = css<PickerComponentSpecialProps>`
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : '195px')};
  height: ${({ $size }) => ($size === 'small' ? 24 : 32)}px;
  border-radius: 8px;
  border-color: #bdc5db;
  position: relative;

  &:not(.ant-picker-small) {
    padding: 3px 7px 3px;
  }

  & input {
    line-height: 24px;
    color: ${colors.gray900};
  }

  & input::placeholder,
  & .ant-picker-suffix {
    color: ${colors.gray400};
  }

  &:hover,
  &.ant-picker-focused {
    border-color: ${colors.blue500};
  }

  &.ant-picker.ant-picker-disabled {
    background: #eaedf5;
    color: ${colors.gray400};

    & .ant-picker-suffix {
      color: ${colors.gray400};
    }
  }
`;

const selectRange = css`
  .ant-picker-cell-in-view.ant-picker-cell-in-range::before {
    background-color: ${colors.blue100};
  }

  .ant-picker-cell-in-view.ant-picker-cell-in-range.ant-picker-cell-end::before,
  .ant-picker-cell-in-view.ant-picker-cell-in-range:last-child::before {
    border-end-end-radius: 8px;
    border-start-end-radius: 8px;
  }

  .ant-picker-cell-in-view.ant-picker-cell-in-range.ant-picker-cell-start::before,
  .ant-picker-cell-in-view.ant-picker-cell-in-range:first-child::before {
    border-start-start-radius: 8px;
    border-end-start-radius: 8px;
  }
`;

const range = css`
  .ant-picker-clear {
    right: 8px;
  }

  .ant-picker-range-separator {
    color: ${colors.gray400};
    padding: 4px;
  }

  ${selectRange}
`;

export const getStyledDatePicker = <T = Dayjs>(picker: DataPickerComponentProps<T>) => styled<
  DataPickerComponentProps<T>
>(picker)`
  ${input}
  ${dropdown}
`;

export const getStyledDateRangePicker = <T = Dayjs>(picker: RangePickerComponentProps<T>) => styled<
  RangePickerComponentProps<T>
>(picker)`
  ${input}
  ${dropdown}
    ${range}

    & {
    min-width: 256px;
  }

  .ant-picker-suffix {
    margin-left: auto;
    padding-left: 4px;
  }

  .ant-picker-input input {
    text-overflow: ellipsis;
  }

  .ant-picker-active-bar {
    display: none;
  }
`;

export const getStyledTimePicker = <T = Dayjs>(picker: TimePickerComponentProps<T>) => styled<
  TimePickerComponentProps<T>
>(picker)`
  path {
    pointer-events: none;
  }

  span[aria-label='clock-circle'] {
    pointer-events: all;
    font-size: 16px;
  }

  .ant-picker-dropdown {
    position: relative;
  }

  ${input}
`;

export const StyledDatePicker = getStyledDatePicker(DatePicker);

export const StyledRangePicker = getStyledDateRangePicker(DatePicker.RangePicker);

export const StyledTimePicker = getStyledTimePicker(DatePicker.TimePicker);

export const WrapperDatePicker = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const Styledlabel = styled.label`
  font-size: 12px;
  line-height: 20px;
  color: ${colors.gray900};
`;
